import {
  captureConsoleIntegration,
  extraErrorDataIntegration,
  httpClientIntegration,
  rewriteFramesIntegration,
} from '@sentry/integrations';
import type { Integration } from '@sentry/types/types/integration';
import * as Sentry from '@sentry/vue';
import { App } from 'vue';
import { Router } from 'vue-router';
import { AppEnvironment } from '@pn/common';

type EnabledIntegrations = {
  browserTracing?: boolean;
  replay?: boolean;
  feedback?: boolean;
  captureConsole?: boolean;
  extraErrorData?: boolean;
  httpClient?: boolean;
  rewriteFrames?: boolean;
};

const DEFAULT_INTEGRATIONS: EnabledIntegrations = {
  browserTracing: true,
  replay: true,
  feedback: false,
  captureConsole: false, // NOTE: Приводит к рекурсивным вызовам по неизвестной причине
  extraErrorData: true,
  httpClient: true,
  rewriteFrames: false, // NOTE: Не преобразовывает как следует
};

const makeFeedbackIntegration = (): Integration => {
  return Sentry.feedbackIntegration({
    showBranding: false,
    colorScheme: 'light',
    useSentryUser: {
      email: 'email',
      name: 'username',
    },
    buttonLabel: 'Сообщить об ошибке',
    formTitle: 'Сообщить об ошибке',
    isNameRequired: true,
    nameLabel: 'Имя',
    namePlaceholder: 'Иванов Иван Иванович',
    isEmailRequired: true,
    emailLabel: 'Электронная почта',
    emailPlaceholder: 'Ivanov_II@corppn.ru',
    messageLabel: 'Описание ошибки',
    messagePlaceholder: 'Опишите что произошло',
    cancelButtonLabel: 'Отмена',
    submitButtonLabel: 'Отправить',
    successMessageText: 'Спасибо',
  });
};

export function sentryClientInit(
  app: App,
  router: Router,
  dsn: string,
  env: AppEnvironment,
  release?: string,
  enabledIntegrations: EnabledIntegrations = DEFAULT_INTEGRATIONS,
) {
  const sentryOptions: Parameters<typeof Sentry.init>[0] = {
    enabled: Boolean(dsn),
    dsn: dsn,
    environment: env,
    release: release || env,
    sampleRate: 1.0,
    attachStacktrace: true,
    enableTracing: true,
    tracesSampleRate: 1.0,
    autoSessionTracking: true,
    app,
  };

  const integrations$: EnabledIntegrations = {
    ...DEFAULT_INTEGRATIONS,
    ...enabledIntegrations,
  };
  const integrations: Integration[] = [];

  if (integrations$.browserTracing) {
    sentryOptions.trackComponents = true;

    integrations.push(
      Sentry.browserTracingIntegration({
        router,
        routeLabel: 'path',
      }),
    );
  }

  if (integrations$.replay) {
    sentryOptions.replaysSessionSampleRate = 1.0;
    sentryOptions.replaysOnErrorSampleRate = 1.0;

    integrations.push(
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [window.location.origin, 'https://sigurd01.corppn.ru:18080'],
        networkCaptureBodies: true,
        // useCompression: true,
      }),
    );
  }

  if (integrations$.feedback) {
    integrations.push(makeFeedbackIntegration());
  }

  if (integrations$.captureConsole) {
    integrations.push(
      captureConsoleIntegration({
        levels: ['log', 'warn', 'error'],
      }),
    );
  }

  if (integrations$.extraErrorData) {
    integrations.push(
      extraErrorDataIntegration({
        depth: 3,
        captureErrorCause: true,
      }),
    );
  }

  if (integrations$.httpClient) {
    sentryOptions.sendDefaultPii = true;

    integrations.push(
      httpClientIntegration({
        failedRequestStatusCodes: [
          [400, 499],
          [500, 599],
        ],
      }),
    );
  }

  if (integrations$.rewriteFrames) {
    integrations.push(rewriteFramesIntegration({ root: '../../../../' }));
  }

  sentryOptions.integrations = integrations;

  Sentry.init(sentryOptions);
}
